/**
 * Compute Metrics Component
 *
 * Displays CPU and Memory usage charts as horizontal bar charts.
 * Color-coded based on usage thresholds (green < 70%, yellow 70-85%, red > 85%).
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import EChart from '@/components/EchartCustomized/MapEchart'
import { SCHEMES } from '@/components/EchartCustomized'
import type { ComputeCapacity, HostCapacity } from '../../types'

interface ComputeMetricsProps {
    compute: ComputeCapacity
}

/** Get bar color based on usage percentage */
const getBarColor = (pct: number): string => {
    if (pct >= 85) return SCHEMES.error[0]
    if (pct >= 70) return SCHEMES.warning[0]
    return SCHEMES.success[0]
}

/** Create bar chart options for host metrics */
const createHostBarChart = (
    hosts: HostCapacity[],
    metricType: 'cpu' | 'memory'
) => {
    // Limit to top 10 hosts by usage
    const sortedHosts = [...hosts]
        .sort((a, b) =>
            metricType === 'cpu'
                ? b.cpu_usage_pct - a.cpu_usage_pct
                : b.memory_usage_pct - a.memory_usage_pct
        )
        .slice(0, 10)

    const hostNames = sortedHosts.map((h) => h.name)
    const values = sortedHosts.map((h) =>
        metricType === 'cpu' ? h.cpu_usage_pct : h.memory_usage_pct
    )
    const colors = values.map((v) => getBarColor(v))

    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params: any) => {
                const data = params[0]
                return `${data.name}: ${data.value.toFixed(1)}%`
            },
        },
        grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            top: '3%',
            containLabel: true,
        },
        xAxis: {
            type: 'value',
            max: 100,
            axisLabel: {
                formatter: '{value}%',
            },
        },
        yAxis: {
            type: 'category',
            data: hostNames,
            axisLabel: {
                width: 100,
                overflow: 'truncate',
            },
        },
        series: [
            {
                type: 'bar',
                data: values.map((value, index) => ({
                    value,
                    itemStyle: {
                        color: colors[index],
                        borderRadius: [0, 4, 4, 0],
                    },
                })),
                label: {
                    show: true,
                    position: 'right',
                    formatter: '{c}%',
                    fontSize: 11,
                },
            },
        ],
    }
}

const ComputeMetrics = ({ compute }: ComputeMetricsProps) => {
    const hosts = compute.hosts || []

    // If no host data, show aggregate stats
    if (hosts.length === 0) {
        return (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
                <Card className="lg:col-span-6 col-span-12">
                    <CardHeader>
                        <CardTitle>CPU Usage</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-center py-8">
                            <div className="text-4xl font-bold text-blue-600">
                                {compute.avg_cpu_usage_pct.toFixed(1)}%
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                                {compute.used_cpu_cores} /{' '}
                                {compute.total_cpu_cores} cores used
                            </p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="lg:col-span-6 col-span-12">
                    <CardHeader>
                        <CardTitle>Memory Usage</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-center py-8">
                            <div className="text-4xl font-bold text-purple-600">
                                {compute.avg_memory_usage_pct.toFixed(1)}%
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                                {compute.used_memory_gb.toFixed(0)} /{' '}
                                {compute.total_memory_gb.toFixed(0)} GB used
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        )
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
            <Card className="lg:col-span-6 col-span-12">
                <CardHeader>
                    <CardTitle>CPU Usage by Host</CardTitle>
                </CardHeader>
                <CardContent>
                    <EChart
                        options={createHostBarChart(hosts, 'cpu')}
                        style={{ height: 250 }}
                    />
                </CardContent>
            </Card>
            <Card className="lg:col-span-6 col-span-12">
                <CardHeader>
                    <CardTitle>Memory Usage by Host</CardTitle>
                </CardHeader>
                <CardContent>
                    <EChart
                        options={createHostBarChart(hosts, 'memory')}
                        style={{ height: 250 }}
                    />
                </CardContent>
            </Card>
        </div>
    )
}

export default ComputeMetrics
